import { rtkApi, GET_ASSIGNMENT_SUBMISSION, GET_ASSIGNMENTS } from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import { createFormData } from '@/shared/lib/utils/createFormData';
import {
  IAssignmentsRequest,
  IAssignmentsResponse,
  IAssignmentContextRequest,
  IAssignmentContextResponse,
  IAssignmentSubmissionQueryRequest,
  IAssignmentSubmissionQueryResponse,
  IAssignmentSubmissionRequest,
  IAssignmentSubmissionResponse,
} from './types';

const assignmentApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getAssignments: build.query<IAssignmentsResponse, IAssignmentsRequest>({
      query: (params) => ({
        url: '/student/assignments',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<IAssignmentsResponse>) => response.data,
      providesTags: [GET_ASSIGNMENTS],
    }),
    getAssignmentContext: build.query<IAssignmentContextResponse, IAssignmentContextRequest>({
      query: (params) => ({
        url: '/student/assignments/context',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<IAssignmentContextResponse>) =>
        response.data,
    }),
    getAssignmentSubmission: build.query<
      IAssignmentSubmissionQueryResponse,
      IAssignmentSubmissionQueryRequest
    >({
      query: (params) => ({
        url: '/student/assignments/submission',
        params,
      }),
      transformResponse: (response: SuccessApiResponse<IAssignmentSubmissionQueryResponse>) =>
        response.data,
      providesTags: [GET_ASSIGNMENT_SUBMISSION],
    }),
    submitSubmission: build.mutation<
      IAssignmentSubmissionResponse,
      { assignmentId: number; data: IAssignmentSubmissionRequest }
    >({
      query: ({ assignmentId, data }) => {
        const payload: IAssignmentSubmissionRequest = { text: data.text };
        if (data.files?.length) {
          payload.files = data.files;
        }
        if (data.groupLessonId) {
          payload.groupLessonId = data.groupLessonId;
        }
        if (data.groupExamResubmissionId) {
          payload.groupExamResubmissionId = data.groupExamResubmissionId;
        }

        return {
          url: `/student/assignments/${assignmentId}/submission`,
          method: 'POST',
          body: createFormData(payload),
        };
      },
      transformResponse: (response: SuccessApiResponse<IAssignmentSubmissionResponse>) =>
        response.data,
      invalidatesTags: [GET_ASSIGNMENT_SUBMISSION, GET_ASSIGNMENTS],
    }),
    updateSubmission: build.mutation<
      IAssignmentSubmissionResponse,
      { assignmentId: number; data: IAssignmentSubmissionRequest }
    >({
      query: ({ assignmentId, data }) => {
        const payload: IAssignmentSubmissionRequest = { text: data.text };
        if (data.files?.length) {
          payload.files = data.files;
        }

        return {
          url: `/student/assignments/${assignmentId}/submission`,
          method: 'PUT',
          body: createFormData(payload),
        };
      },
      transformResponse: (response: SuccessApiResponse<IAssignmentSubmissionResponse>) =>
        response.data,
      invalidatesTags: [GET_ASSIGNMENT_SUBMISSION, GET_ASSIGNMENTS],
    }),
  }),
});

export const {
  useGetAssignmentsQuery,
  useGetAssignmentContextQuery,
  useGetAssignmentSubmissionQuery,
  useSubmitSubmissionMutation,
  useUpdateSubmissionMutation,
} = assignmentApi;
